/**
 * AI Quick-Start Presets — sensible starter prompts for each
 * (productType, Shopify column) pair.
 *
 * The presets are wired into the MapperInspector via `presetsForColumn`. When
 * the user selects a Shopify column that has a preset AND the current rule is
 * not already an AI rule, the inspector surfaces a violet "Use AI for this"
 * card. Clicking it replaces the current rule with `preset.build(productType)`
 * and flips the kind picker to AI so the AIEditor renders pre-filled.
 *
 * Design notes:
 * - All presets default to `anthropic` + `claude-sonnet-4-6` with reasoning
 *   off — that's the same "balanced default" the catalogue advertises and it
 *   keeps per-row cost predictable for non-technical users.
 * - `maxTokens` is sized to the column: 256 for short fields (Title, Tags,
 *   SEO Title) and 512 for longer body/description fields.
 * - `/Header` tokens reference the actual saree.xlsx source headers (yes,
 *   "Product Tittle" is the typo in the source — we honour it so the prompt
 *   resolves cleanly). Lehenga/Suit/Kurta profiles share the same source
 *   header names per the seeded profiles, so these presets carry through.
 */

import type { Rule, ProductType } from "@/lib/mapper/types";

type AIRule = Extract<Rule, { kind: "ai" }>;

export type AIPreset = {
  /** Shopify column this preset targets. */
  column: string;
  /** Short label for the button, e.g. "Catchy title". */
  label: string;
  /** One-line description so the user knows what the prompt produces. */
  description: string;
  /** The Rule that will replace the column's current rule when applied. */
  build: (productType: ProductType) => AIRule;
};

// ---------------------------------------------------------------------------
// Defaults shared across every preset.
// ---------------------------------------------------------------------------

const BASE: Omit<AIRule, "prompt" | "maxTokens"> = {
  kind: "ai",
  provider: "anthropic",
  model: "claude-sonnet-4-6",
  reasoning: { kind: "off" },
};

function shortRule(prompt: string): AIRule {
  return { ...BASE, prompt, maxTokens: 256 };
}

function longRule(prompt: string): AIRule {
  return { ...BASE, prompt, maxTokens: 512 };
}

// ---------------------------------------------------------------------------
// Per-column prompt builders.
//
// Each helper takes the productType and returns the prompt string only — the
// preset's `build` wraps it in shortRule/longRule with the right maxTokens.
// ---------------------------------------------------------------------------

function titlePrompt(pt: ProductType): string {
  switch (pt) {
    case "Saree":
      return "Write a catchy product title for a /COLOR /FABRIC saree with /WORK work. Brand voice: elegant, festive. Under 70 characters. No quotes, no fluff. Just the title text.";
    case "Lehenga":
      return "Write a catchy product title for a /COLOR /FABRIC lehenga with /WORK work. Brand voice: elegant, festive, bridal. Under 70 characters. Plain text only.";
    case "Suit":
    case "Kurta":
    case "Generic":
    case "Unknown":
    default:
      return "Write a catchy product title from the description: /Product Description. Brand voice: elegant. Under 70 characters. Plain text.";
  }
}

function bodyPrompt(pt: ProductType): string {
  switch (pt) {
    case "Saree":
      return "Write a Shopify product description (HTML, 3-4 sentences) for a /COLOR /FABRIC saree with /WORK work. Highlight craftsmanship and occasion. Wrap in a single <p> tag. Brand voice: elegant, festive. No markdown.";
    case "Lehenga":
      return "Write a Shopify product description (HTML, 3-4 sentences) for a /COLOR /FABRIC lehenga with /WORK work. Highlight craftsmanship and bridal/festive occasion. Wrap in a single <p> tag. Brand voice: elegant, festive, bridal. No markdown.";
    case "Suit":
    case "Kurta":
    case "Generic":
    case "Unknown":
    default:
      return "Write a Shopify product description (HTML, 3-4 sentences) from this source text: /Product Description (Kindly share detailed description). Wrap in a single <p> tag. Brand voice: elegant. No markdown.";
  }
}

function tagsPrompt(pt: ProductType): string {
  switch (pt) {
    case "Saree":
      return "Generate 6-8 SEO tags for a /COLOR /FABRIC saree with /WORK work. Comma-separated, lowercase, hyphens for multi-word. Include the fabric, color, and occasion (festival, party, wedding). No '#'. Example format: silk-saree, festive, banarasi";
    case "Lehenga":
      return "Generate 6-8 SEO tags for a /COLOR /FABRIC lehenga with /WORK work. Comma-separated, lowercase, hyphens for multi-word. Include the fabric, color, and occasion (wedding, bridal, reception). No '#'. Example format: bridal-lehenga, wedding, silk";
    case "Suit":
    case "Kurta":
    case "Generic":
    case "Unknown":
    default:
      return "Generate 6-8 SEO tags from /Product Description. Comma-separated, lowercase, hyphens for multi-word. Include the fabric, color, and occasion. No '#'.";
  }
}

function seoTitlePrompt(pt: ProductType): string {
  const example =
    pt === "Lehenga"
      ? "lehenga"
      : pt === "Suit"
        ? "suit"
        : pt === "Kurta"
          ? "kurta"
          : "saree";
  return `Write a 50-60 character SEO title for: /Product Tittle. Include the main keyword (${example}). Plain text, no quotes.`;
}

function seoDescriptionPrompt(_pt: ProductType): string {
  return "Write a 140-155 character meta description for: /Product Tittle. Mention /COLOR, /FABRIC, and craftsmanship. Encourage a click. Plain text.";
}

function packContainsPrompt(_pt: ProductType): string {
  return "Convert this pack-contains string '/Pack Contains' into a clear English sentence. Example: '1 saree::1 blouse' → 'Includes 1 saree and 1 unstitched blouse piece.'";
}

function shortDescriptionPrompt(pt: ProductType): string {
  const noun =
    pt === "Lehenga"
      ? "lehenga"
      : pt === "Suit"
        ? "suit"
        : pt === "Kurta"
          ? "kurta"
          : "saree";
  return `Write a single-sentence summary (max 100 chars) of this /COLOR /FABRIC ${noun} with /WORK work. Plain text.`;
}

// ---------------------------------------------------------------------------
// The catalogue. Order here = order rendered in the inspector.
// ---------------------------------------------------------------------------

export const AI_PRESETS: AIPreset[] = [
  {
    column: "Title",
    label: "Catchy title",
    description:
      "Write a short, on-brand product title from the source row's color, fabric, and work.",
    build: (pt) => shortRule(titlePrompt(pt)),
  },
  {
    column: "Body (HTML)",
    label: "Product description",
    description:
      "Generate a 3-4 sentence HTML product description highlighting craftsmanship and occasion.",
    build: (pt) => longRule(bodyPrompt(pt)),
  },
  {
    column: "Tags",
    label: "SEO tags",
    description:
      "Produce 6-8 comma-separated SEO tags covering fabric, color, and occasion.",
    build: (pt) => shortRule(tagsPrompt(pt)),
  },
  {
    column: "SEO Title",
    label: "SEO title",
    description:
      "Compose a 50-60 character SEO title with the main product keyword.",
    build: (pt) => shortRule(seoTitlePrompt(pt)),
  },
  {
    column: "SEO Description",
    label: "Meta description",
    description:
      "Compose a 140-155 character meta description that mentions color, fabric, and craftsmanship.",
    build: (pt) => longRule(seoDescriptionPrompt(pt)),
  },
  {
    column: "Pack contains (product.metafields.custom.pack_contains)",
    label: "Pack contains sentence",
    description:
      "Turn the raw 'pack contains' string into a clear English sentence.",
    build: (pt) => shortRule(packContainsPrompt(pt)),
  },
  {
    column: "Short Description (product.metafields.custom.short_description)",
    label: "Short summary",
    description:
      "Write a single-sentence summary (≤100 chars) of the product.",
    build: (pt) => shortRule(shortDescriptionPrompt(pt)),
  },
];

/** Return every preset that targets the given Shopify column. */
export function presetsForColumn(column: string): AIPreset[] {
  return AI_PRESETS.filter((p) => p.column === column);
}
